import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabase'
import Announcement from './Announcement'
import RankPopup from './RankPopup'
import { getRankInfo } from '../utils/rankUtils'

const Header = () => {
  const { user, signOut } = useAuth()
  const [profile, setProfile] = useState(null)
  const [showRankPopup, setShowRankPopup] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    if (user) {
      getProfile()
    }
  }, [user])

  const getProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) throw error
      setProfile(data)
    } catch (error) {
      console.error('Error loading profile:', error.message)
    }
  }

  const getMembershipEmoji = (membership) => {
    switch (membership) {
      case 'free':
        return <span className="text-gray-400">🆓</span>
      case 'vip':
        return <span className="text-yellow-400">👑</span>
      case 'lifetime':
        return <span className="text-green-400">♾️</span>
      case 'admin':
        return <span className="text-red-400">⚡</span>
      default:
        return null
    }
  }

  const handleLogout = async () => {
    try {
      const { error } = await signOut()
      if (error) throw error
      // รอสักครู่ก่อนเปลี่ยนหน้า
      setTimeout(() => {
        window.location.href = '/'
      }, 500)
    } catch (error) {
      console.error('Error logging out:', error.message)
    }
  }

  const renderNavLinks = () => (
    <>
      <Link to="/dashboard" className="text-gray-300 hover:text-white transition-colors">Dashboard</Link>
      {profile?.membership === 'free' && (
        <Link to="/generator" className="text-gray-300 hover:text-white transition-colors">สร้าง Prompt</Link>
      )}
      {(profile?.membership === 'admin' || profile?.membership === 'vip' || profile?.membership === 'lifetime') && (
        <>
          <Link to="/ready-prompt" className="text-yellow-400 hover:text-yellow-300 transition-colors">Ready Prompt</Link>
        </>
      )}
      {profile?.membership === 'admin' && (
        <>
          <Link 
            to="/admin" 
            className="text-red-400 hover:text-red-300 transition-colors flex items-center gap-2"
          >
            <span>Admin</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </Link>
          <Link 
            to="/admin/tools" 
            className="text-red-400 hover:text-red-300 transition-colors flex items-center gap-2"
          >
            <span>Tools</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </Link>
          <Link 
            to="/report" 
            className="text-red-400 hover:text-red-300 transition-colors flex items-center gap-2"
          >
            <span>Report</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </Link>
        </>
      )}
      {(profile?.membership === 'admin' || profile?.membership === 'vip' || profile?.membership === 'lifetime') && (
        <Link 
          to="/vip"
          className="bg-yellow-500 text-black px-4 py-1.5 rounded-full font-semibold hover:bg-yellow-400 transition-colors flex items-center gap-1"
        >
          <span>VIP</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        </Link>
      )}
    </>
  )

  return (
    <>
      <header className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center">
                <span className="text-xl font-bold bg-gradient-to-r from-red-600 via-rose-500 to-red-400 text-transparent bg-clip-text hover:from-red-500 hover:via-rose-400 hover:to-red-300 transition-all duration-300">
                  AI Unlocked
                </span>
              </Link>
              
              <nav className="hidden md:flex items-center gap-6">
                {renderNavLinks()}
              </nav>
            </div>

            <div className="flex items-center gap-4">
              {user && profile && (
                <div className="flex items-center gap-4">
                  <div className="hidden md:flex items-center gap-2">
                    {getMembershipEmoji(profile.membership)}
                    <span className="text-gray-300">
                      {profile.membership === 'free' && 'ฟรี'}
                      {profile.membership === 'vip' && 'VIP'}
                      {profile.membership === 'lifetime' && 'Lifetime'}
                      {profile.membership === 'admin' && 'Admin'}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="text-gray-300 hover:text-white transition-colors hidden md:block"
                  >
                    ออกจากระบบ
                  </button>
                </div>
              )}

              {/* แฮมเบอร์เกอร์เมนูสำหรับมือถือ */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-gray-300 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* เมนูมือถือ */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4">
              <nav className="flex flex-col gap-4">
                {renderNavLinks()}
                {user && profile && (
                  <>
                    <div className="flex items-center gap-2 text-gray-300">
                      {getMembershipEmoji(profile.membership)}
                      <span>{profile.display_name || 'สมาชิก'}</span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      ออกจากระบบ
                    </button>
                  </>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      <Announcement />
      {showRankPopup && <RankPopup onClose={() => setShowRankPopup(false)} />}
    </>
  )
}

export default Header